import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Autocomplete } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import AuthPageFrame from '../components/AuthPageFrame';
import AuthLegalNotice from '../components/AuthLegalNotice';

const Signup: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [schoolEmail, setSchoolEmail] = useState('');
  const [educationLevel, setEducationLevel] = useState('');
  const [purpose, setPurpose] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const navigate = useNavigate();


  const submit = async () => {
    setError(null);
    if (!name || !email || !password || !schoolName || !educationLevel || !purpose || !address || !city || !country) {
      setError('Please fill in all required fields (School Email is optional)');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/register', { 
        name, 
        email, 
        password,
        referralCode: referralCode.trim().toUpperCase(),
        schoolName,
        schoolEmail,
        educationLevel,
        purpose,
        address,
        city,
        country
      });
      setLoading(false);
      setSuccessOpen(true);
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || 'Registration failed');
    }
  };

  return (
    <>
      <AuthPageFrame mode="signup">
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: 1.2 }}>
          GET STARTED
        </Typography>
        <Typography component="h1" variant="h4" sx={{ mt: 0.5, mb: 1, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700 }}>
          Create your account
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
          Set up your profile to access A3 learning and research services.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Required Fields */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: '#666' }}>
          Required Information
        </Typography>
        <TextField 
          label="Full name" 
          fullWidth 
          value={name} 
          onChange={e => setName(e.target.value)} 
          sx={{ mb: 2 }}
          required
        />
        <TextField 
          label="Email" 
          type="email"
          fullWidth 
          value={email} 
          onChange={e => setEmail(e.target.value)} 
          sx={{ mb: 2 }}
          required
        />
        <TextField 
          label="Password" 
          type="password" 
          fullWidth 
          value={password} 
          onChange={e => setPassword(e.target.value)} 
          sx={{ mb: 3 }}
          required
        />
        <TextField
          label="Referral code (optional)"
          fullWidth
          value={referralCode}
          onChange={(event) => setReferralCode(event.target.value.slice(0, 8))}
          inputProps={{ maxLength: 8, autoCapitalize: 'characters' }}
          helperText="Enter an existing A3T code to support the person who referred you."
          sx={{ mb: 2 }}
        />

        {/* Educational Information */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>
          Educational Information
        </Typography>
        <Autocomplete
          freeSolo={false}
          options={[
            'Prestige High School',
            'Green Valley High School',
            'Riverside High School',
            'St. Paul High School',
            'Kingsbridge High School',
            'Evergreen High School',
            'Sunrise High School',
          ]}
          value={schoolName}
          onChange={(_, newValue) => setSchoolName(newValue || '')}
          onInputChange={(_, value) => setSchoolName(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="School/Institution Name *"
              required
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Search school name"
            />
          )}
        />
        <TextField 
          label="School/Institution Email" 
          type="email"
          fullWidth 
          value={schoolEmail} 
          onChange={e => setSchoolEmail(e.target.value)} 
          sx={{ mb: 2 }}
          placeholder="e.g., student@university.edu"
        />
        
        <FormControl sx={{ mb: 2, width: '100%' }} size="small" required>
          <InputLabel>Education Level *</InputLabel>
          <Select
            value={educationLevel}
            label="Education Level *"
            onChange={(e) => setEducationLevel(e.target.value)}
          >
            <MenuItem value="">Select education level</MenuItem>
            <MenuItem value="high-school">High School</MenuItem>
            <MenuItem value="associate">Associate Degree</MenuItem>
            <MenuItem value="bachelor">Bachelor's Degree</MenuItem>
            <MenuItem value="master">Master's Degree</MenuItem>
            <MenuItem value="phd">PhD</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 3, width: '100%' }} size="small" required>
          <InputLabel>Purpose *</InputLabel>
          <Select
            value={purpose}
            label="Purpose *"
            onChange={(e) => setPurpose(e.target.value)}
          >
            <MenuItem value="">Select primary purpose</MenuItem>
            <MenuItem value="research">Research</MenuItem>
            <MenuItem value="learning">Learning</MenuItem>
            <MenuItem value="professional">Professional Development</MenuItem>
            <MenuItem value="hobby">Hobby/Personal Interest</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        {/* Location Information */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: '#666' }}>
          Location Information
        </Typography>
        <TextField 
          label="Address *" 
          fullWidth 
          value={address} 
          onChange={e => setAddress(e.target.value)} 
          sx={{ mb: 2 }}
          placeholder="Street address"
          required
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <TextField 
            label="City *" 
            value={city} 
            onChange={e => setCity(e.target.value)} 
            placeholder="City"
            required
          />
          <TextField 
            label="Country *" 
            value={country} 
            onChange={e => setCountry(e.target.value)} 
            placeholder="Country"
            required
          />
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <AuthLegalNotice creatingAccount />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
          <Button 
            variant="contained" 
            onClick={submit} 
            disabled={loading}
            size="large"
            sx={{ minHeight: 48, textTransform: 'none', fontWeight: 700 }}
          >
            {loading ? 'Creating...' : 'Create account'}
          </Button>
          <Button variant="text" onClick={() => navigate('/login')}>Already have an account?</Button>
        </Box>
      </AuthPageFrame>

      <Dialog open={successOpen} onClose={() => {}} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.3rem', color: '#2e7d32' }}>
          ✓ Registration Successful
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2, mb: 2, fontSize: '1rem' }}>
            <strong>Welcome!</strong> Your account has been created successfully.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Please click OK below to proceed to the sign-in page.
          </DialogContentText>
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            You can now log in with your email and password to access the platform and start using Einstein, our AI assistant.
          </DialogContentText>
        </DialogContent>
          <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => { setSuccessOpen(false); navigate('/login'); }} fullWidth>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Signup;
